import React, { useEffect, useRef, useState, useMemo } from "react";
import { Line } from "react-chartjs-2";
import moment from "moment/moment";
import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Title,
  Legend,
} from "chart.js";
import getValueSalesChartConfig from "../../configs/getValueSalesChartConfig";
import validFilter from "../../utils/validFilter";
import ValueSalesChartFilterV2 from "../../components/filters/ValueSalesChartFilterV2";
import useFetch from "../../../../../hooks/fetchMSSQL-hook";
import { PLNFormatterPlugin } from "../plugins/PLNFormatterPlugin";

// Register plugins conditionally
ChartJS.register(
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Title,
  Legend
);

// Utility function to create percentage chart options
const getPercentageChartOptions = () => ({
  responsive: true,
  plugins: {
    plnFormatter: false, // Explicitly disable PLN formatter
    legend: {
      position: 'top',
      labels: {
        generateLabels(chart) {
          return chart.data?.datasets?.map((dataset, i) => {
            let dateLabel = dataset?.label?.replace(/^\b(\d{4}-\d{2}-\d{2})\b.*/, '$1')
            if (dataset.backgroundColor === "red") {
              dateLabel = "Bieżąca godzina"
            } else {
              dateLabel = dataset?.label?.replace(/^\b(\d{4}-\d{2}-\d{2})\b.*/, '$1')
            }
              
            return {
              text: dateLabel,
              fillStyle: dataset.backgroundColor,
              strokeStyle: dataset.borderColor,
              lineWidth: 1,
              hidden: !chart.isDatasetVisible(i),
              datasetIndex: i
            };
          });
        },
      }
    },
    title: {
      display: true,
      text: "Sprzedaż bieżąca (%)",
    },
    tooltip: {
      mode: "index",
      intersect: false,
      enabled: true,
      callbacks: {
        label: (ctx) => {
          const dataset = ctx.chart.data.datasets[ctx.datasetIndex];
          const value = ctx.raw;
          let label = dataset.label?.replace(/^\b(\d{4}-\d{2}-\d{2})\b.*/, '$1');
          
          if (dataset.backgroundColor === "red")
            label = "Bieżąca godzina"
          
          return `${label}: ${value}%`;
        },
      },
    },
  },
  scales: {
    y: {
      beginAtZero: true,
      max: 100,
      ticks: {
        callback: function(value) {
          return value + '%';
        }
      }
    }
  }
});

// Utility functions for plugin management
const registerPLNPlugin = () => {
  if (!ChartJS.registry.plugins.get('plnFormatter')) {
    ChartJS.register(PLNFormatterPlugin);
  }
};

const unregisterPLNPlugin = () => {
  try {
    ChartJS.unregister(PLNFormatterPlugin);
  } catch (error) {
    // Plugin might not be registered, ignore error
  }
};

const createChartOptions = (usePercentageFormat = false) => {
  if (usePercentageFormat) {
    // Ensure PLN plugin is unregistered for percentage charts
    unregisterPLNPlugin();
    return getPercentageChartOptions();
  } else {
    // Register PLN plugin for currency charts
    registerPLNPlugin();
    return getValueSalesChartConfig();
  }
};

export function ValueSalesChart({ machines = [], usePercentageFormat = false }) {
  const chartRef = useRef(null);
  const [sellData, setSellData] = useState([]);
  const { fetchMssqlApi } = useFetch();
  const isMounted = useRef(true);
  const currentHour = useRef(moment().hour());
  
  const [sellDataFilter, setSellDataFilter] = useState({
    fromCreateDateTime: moment().subtract(1, "W").format("YYYY-MM-DD"),
    toCreateDateTime: moment().format("YYYY-MM-DD"),
    paymentType: "1",
    machineName: null
  });

  // Cleanup plugins when component unmounts
  useEffect(() => {
    return () => {
      isMounted.current = false;
      if (usePercentageFormat) {
        unregisterPLNPlugin();
      }
    };
  }, [usePercentageFormat]);

  const getMachineSerialNo = ({ machineName }) => {
    if (!machineName || !machines.length) return null;

    const machine = machines.find(
      ({ Name, SerialNo }) => `${Name} (${SerialNo})` === machineName
    );
    return machine?.SerialNo || null;
  };
  

  const getComparingSalesData = async () => {
    try {
      await validFilter(sellDataFilter);
      fetchMssqlApi(
        "stat/sales",
        {
          method: "POST",
          data: {
            ...sellDataFilter,
            machineSerialNo: getMachineSerialNo(sellDataFilter),
          },
          hideNotification: true,
        },
        (data) => {
          if (isMounted.current) {
            setSellData(data);
          }
        },
        (err) => {
          if (isMounted.current) {
            console.log(err);
          }
        }
      );
    } catch (err) {
      if (isMounted.current) {
        console.log(err);
      }
    }
  };

  const handleValueSalesChartFilterChange = (evt) => {
    const type = evt.target?.value;
    setSellDataFilter((prev) => ({
      ...prev,
      paymentType: type,
    }));
  };

  const handleChangeMachine = (evt) => {
    const machineName = evt.target?.value || "";
    setSellDataFilter((prev) => ({
      ...prev,
      machineName,
    }));

    const serialNo = getMachineSerialNo({ machineName });

    if (serialNo) {
      validFilter({ ...sellDataFilter, machineName }).then(() => {
        if (isMounted.current) {
          fetchMssqlApi(
            "stat/sales",
            {
              method: "POST",
              data: {
                ...sellDataFilter,
                machineName,
                machineSerialNo: serialNo,
              },
              hideNotification: true,
            },
            (data) => {
              if (isMounted.current) {
                setSellData(data);
              }
            },
            (err) => {
              if (isMounted.current) {
                console.log(err);
              }
            }
          );
        }
      });
    }
  };

  useEffect(() => {
    getComparingSalesData();
  }, []);

  useEffect(() => {
    getComparingSalesData();
  }, [sellDataFilter.paymentType]);

  const {
    firstBarKey,
    secondBarKey,
    dataset1,
    dataset2,
    dataset3,
    countDataset1,
    countDataset2,
    countDataset3
  } = useMemo(() => {
    if (!sellData || !sellData.length) {
      return {
        firstBarKey: moment().format("YYYY-MM-DD"),
        secondBarKey: moment().subtract(1, "week").format("YYYY-MM-DD"),
        dataset1: [],
        dataset2: [],
        dataset3: [],
        countDataset1: [],
        countDataset2: [],
        countDataset3: []
      };
    }

    const secondBarKey =
      sellData.find(i => Boolean(i.PrevDate) !== false)?.PrevDate ||
      moment().subtract(1, "week").format("YYYY-MM-DD");

    const firstBarKey =
      sellData.find(i => Boolean(i.CurrentDate) !== false)?.CurrentDate ||
      moment().format("YYYY-MM-DD");

    const dataset2 = sellData.map(i => i?.PrevSummaryValue || 0);

    const dataset1 = sellData.map((i, idx) => {
      if (!Object.keys(i).length && idx + 1 < currentHour.current) return 0;
      if (idx + 1 > currentHour.current) return null;
      if (i.PrevHour < currentHour.current && !i.CurrentSummaryValue) return 0;
      if (i.PrevHour === currentHour.current) return null;
      if (i.PrevHour > currentHour.current) return null;
      return i.CurrentSummaryValue;
    });

    const dataset3 = sellData.map(i =>
      i.PrevHour === currentHour.current ? +i.CurrentSummaryValue || 0 : null
    );
    
    const countDataset2 = sellData.map(i => +i?.PrevSummaryCount || 0);
    const countDataset1 = sellData.map(i => +i?.CurrentSummaryCount || 0);


    const countDataset3 = (() => {
      let a = sellData.map((i, idx) =>
        i.PrevHour === currentHour.current
          ? +i.CurrentSummaryCount || (idx === currentHour.current ? 0 : null)
          : null
      );
      if (!a.length) {
        a = Array.from({ length: 24 }, (_, idx) =>
          idx === currentHour.current ? 0 : null
        );
      }
      return a;
    })();

    return {
      firstBarKey,
      secondBarKey,
      dataset1,
      dataset2,
      dataset3,
      countDataset1,
      countDataset2,
      countDataset3
    };
  }, [sellData]);

  const datasets = useMemo(() => {
    const barColors = ["#C5D8FF", "#0140BE"];
    const result = [];
    
    if (secondBarKey) {
      result.push({
        label: secondBarKey || null,
        data: dataset2,
        backgroundColor: "#c5d8ff81",
        borderColor: barColors[0],
        countData: countDataset2,
        borderDash: [5, 5],
      });
    }
    
    if (firstBarKey) {
      result.push({
        label: firstBarKey || null,
        data: dataset1,
        countData: countDataset1,
        borderColor: barColors[1],
        backgroundColor: "#0140be80",
      });
    }
    
    if (dataset3 && dataset3.some(value => value !== null)) {
      result.push({
        label: "Bieżąca godzina",  
        data: dataset3,
        countData: countDataset3,
        backgroundColor: "#FF7A00",
        borderColor: "#FF7A00",   
      });
    }
    
    return result;
  }, [
    firstBarKey, 
    secondBarKey, 
    dataset1, 
    dataset2, 
    dataset3, 
    countDataset1, 
    countDataset2, 
    countDataset3
  ]);

  const chartData = useMemo(() => {
    const labels = Array.from({ length: 25 }, (_, hour) =>
      hour === 24 ? "0:00" : `${hour}:00`
    );
    
    return {
      labels,
      datasets,
    };
  }, [datasets]);

  const isEmpty = useMemo(() => {
    return sellData.length === 0 || 
           (dataset1.length === 0 && dataset2.length === 0) || 
           [dataset1, dataset2].flat().every(i => !i);
  }, [sellData, dataset1, dataset2]);

  return (
    <>
      <div>
        <ValueSalesChartFilterV2
          salesChartFilter={sellDataFilter}
          handleChangePaymentType={handleValueSalesChartFilterChange}
          handleSubmit={getComparingSalesData}
          machines={machines}
          handleChangeMachine={handleChangeMachine}
          getData={getComparingSalesData}
        />
      </div>
      {isEmpty ? (
        <div
          className="alert"
          style={{ backgroundColor: "#C5D8FF", fontWeight: "bolder" }}
        >
          Brak danych
        </div>
      ) : (
        <Line ref={chartRef} data={chartData} options={createChartOptions(usePercentageFormat)} />
      )}
    </>
  );
}
